import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function LandlordsPage() {
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const partnershipDetails = [
    {
      icon: 'ri-file-shield-2-line',
      title: 'Guaranteed Lease & Payment',
      color: 'from-teal-500 to-cyan-600',
      details: [
        'Lease term: 2–5 years, providing consistent rental income',
        'Rent is guaranteed every month, even if the property is vacant',
        'First month free for setup and preparation',
        'No security deposit required (optional)'
      ]
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Property Management',
      color: 'from-blue-500 to-indigo-600',
      details: [
        'Full-service management: furnishing, cleaning, bookings, guest communication',
        'Regular inspections and maintenance to preserve property condition',
        'Professional cleaning service between every guest',
        'Smart noise monitoring and property protection systems'
      ]
    },
    {
      icon: 'ri-trophy-line',
      title: 'Benefits to Landlords',
      color: 'from-purple-500 to-pink-600',
      details: [
        'Receive consistent monthly income without the hassle of managing short-term rentals',
        'Zero operational work — ARM handles all guest and property tasks',
        'Flexible lease and partnership options',
        'Reduced risk with guaranteed rent',
        'Professional property care and maintenance',
        'Regular property updates and transparent reporting'
      ]
    }
  ];

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Guaranteed Monthly Rent',
      description: 'Receive consistent, reliable rental income every month regardless of occupancy. We handle all guest payments and assume the financial responsibility.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Property Protection',
      description: 'Smart noise monitoring, regular inspections, and professional oversight ensure your property is protected and maintained to the highest standards.'
    },
    {
      icon: 'ri-user-search-line',
      title: 'Strict Guest Screening',
      description: 'Thorough vetting process for all guests and future long-term tenants, including background checks and verification to protect your investment.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Professional Maintenance',
      description: 'We handle all minor maintenance and coordinate major repairs, keeping your property in excellent condition with regular upkeep.'
    },
    {
      icon: 'ri-sparkling-line',
      title: 'Hotel-Level Cleaning',
      description: 'Professional cleaning service between every guest ensures your property is always pristine and well-maintained.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Complete Management',
      description: 'We handle everything: guest communication, bookings, check-ins, cleaning coordination, and all day-to-day operations.'
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Initial Consultation',
      description: 'We discuss your property, review its potential, and explain our management approach and partnership terms.'
    },
    {
      step: '2',
      title: 'Property Assessment',
      description: 'Our team evaluates your property to determine optimal rental strategy and any needed improvements or furnishings.'
    },
    {
      step: '3',
      title: 'Agreement & Setup',
      description: 'We finalize the partnership agreement, handle furnishing if needed, and prepare your property for guests.'
    },
    {
      step: '4',
      title: 'Professional Launch',
      description: 'Your property goes live with professional photography, optimized listings, and our full management services.'
    },
    {
      step: '5',
      title: 'Ongoing Management',
      description: 'We handle all operations while you receive guaranteed monthly rent and regular property updates.'
    }
  ];

  const protections = [
    {
      icon: 'ri-sound-module-line',
      title: 'Smart Noise Monitoring',
      description: 'Advanced monitoring systems detect excessive noise levels, protecting your property and maintaining good neighbor relations.'
    },
    {
      icon: 'ri-eye-line',
      title: 'Regular Inspections',
      description: 'Scheduled property inspections ensure everything is in order and identify any maintenance needs early.'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Insurance Coverage',
      description: 'Comprehensive insurance protection for your property and our operations, providing additional peace of mind.'
    },
    {
      icon: 'ri-camera-line',
      title: 'Documentation',
      description: 'Detailed photo documentation before and after each guest stay, maintaining clear records of property condition.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section 
        className="relative bg-cover bg-center h-96 flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/c1b8ecc5e3f28c1ed6e78963e5336951.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Partner With ARM</h1>
          <p className="text-2xl text-gray-100 max-w-3xl">
            Maximize your rental income with professional, stress-free property management
          </p>
        </div>
      </section>

      {/* Why Property Owners Choose ARM - NOW APPEARS AFTER THE ACCORDION */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Property Owners Choose ARM</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As a registered LLC with professional standards and proven systems, we offer property owners a reliable, profitable partnership
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">The ARM Advantage</h2>
              <div className="space-y-6 text-lg text-teal-50 leading-relaxed">
                <p>
                  Unlike traditional property management, ARM takes full responsibility for your property's rental performance. We pay you guaranteed rent every month, regardless of occupancy, removing the financial uncertainty from rental property ownership.
                </p>
                <p>
                  Our professional approach combines strict quality standards with advanced technology. From smart noise monitoring to professional cleaning protocols, we protect your investment while maximizing its potential.
                </p>
                <p>
                  As a registered LLC, we operate with full transparency and proper documentation, making us the ideal partner for landlords, business collaborators, and financial institutions. You can trust that your property is in professional, capable hands.
                </p>
                <p className="font-semibold text-white">
                  Partner with ARM and experience truly stress-free property ownership with consistent income and professional care.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 text-gray-900 shadow-2xl">
              <h3 className="text-2xl font-bold mb-6">What You Get</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-double-line text-2xl text-teal-600 flex-shrink-0"></i>
                  <span className="text-lg">Guaranteed monthly rent payments</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-double-line text-2xl text-blue-600 flex-shrink-0"></i>
                  <span className="text-lg">Zero vacancy risk or concern</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-double-line text-2xl text-purple-600 flex-shrink-0"></i>
                  <span className="text-lg">Professional property maintenance</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-double-line text-2xl text-pink-600 flex-shrink-0"></i>
                  <span className="text-lg">Complete guest management</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-double-line text-2xl text-teal-600 flex-shrink-0"></i>
                  <span className="text-lg">Regular property updates and reports</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-double-line text-2xl text-blue-600 flex-shrink-0"></i>
                  <span className="text-lg">Hotel-level property standards</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-double-line text-2xl text-purple-600 flex-shrink-0"></i>
                  <span className="text-lg">Peace of mind and free time</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How We Protect Your Property</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your property is your investment. We treat it with the care and respect it deserves.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {protections.map((protection, index) => (
              <div key={index} className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${protection.icon} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{protection.title}</h3>
                <p className="text-gray-600 leading-relaxed">{protection.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Partnership Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting started with ARM is simple and straightforward
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-teal-300 via-blue-300 to-purple-300 hidden lg:block"></div>
            
            <div className="space-y-12">
              {process.map((item, index) => (
                <div key={index} className={`flex flex-col lg:flex-row gap-8 items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  <div className="flex-1 lg:text-right">
                    {index % 2 === 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg z-10 flex-shrink-0 order-first lg:order-none">
                    <span className="text-2xl font-bold text-white">{item.step}</span>
                  </div>
                  
                  <div className="flex-1">
                    {index % 2 !== 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Where We List Your Property Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Where We List Your Property
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Ali Residential Management (ARM) uses a multi-platform distribution strategy to ensure your property reaches the highest number of potential guests. Instead of relying on only one booking platform, we list and synchronize your rental across all major travel sites.
            </p>
          </div>

          {/* Platform Logos */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-8 mb-16">
            <div className="flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-center">
                <i className="ri-home-heart-line text-5xl text-pink-500 mb-2"></i>
                <p className="text-sm font-semibold text-gray-700">Airbnb</p>
              </div>
            </div>
            <div className="flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-center">
                <i className="ri-hotel-line text-5xl text-blue-600 mb-2"></i>
                <p className="text-sm font-semibold text-gray-700">Booking.com</p>
              </div>
            </div>
            <div className="flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-center">
                <i className="ri-plane-line text-5xl text-yellow-500 mb-2"></i>
                <p className="text-sm font-semibold text-gray-700">Expedia</p>
              </div>
            </div>
            <div className="flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-center">
                <i className="ri-price-tag-3-line text-5xl text-green-600 mb-2"></i>
                <p className="text-sm font-semibold text-gray-700">Priceline</p>
              </div>
            </div>
            <div className="flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-center">
                <i className="ri-building-line text-5xl text-blue-700 mb-2"></i>
                <p className="text-sm font-semibold text-gray-700">VRBO</p>
              </div>
            </div>
            <div className="flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-center">
                <i className="ri-map-pin-line text-5xl text-green-700 mb-2"></i>
                <p className="text-sm font-semibold text-gray-700">TripAdvisor</p>
              </div>
            </div>
            <div className="flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-center">
                <i className="ri-global-line text-5xl text-purple-600 mb-2"></i>
                <p className="text-sm font-semibold text-gray-700">Agoda</p>
              </div>
            </div>
          </div>

          {/* Benefits Description */}
          <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-2xl p-8 md:p-12 mb-12">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                This increases visibility, boosts occupancy, and maximizes monthly income while ARM handles all guest communication, cleaning coordination, screening, and calendar management.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We use automated channel management technology to prevent double-bookings and ensure your property stays fully updated on every platform in real time. This gives landlords, guests, and partners complete confidence in a smooth, professional operation.
              </p>
            </div>
          </div>

          {/* Channel Management Feature Box */}
          <div className="bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-5xl text-white"></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Automated Channel Management</h3>
                <p className="text-lg text-teal-50 leading-relaxed">
                  ARM syncs calendars automatically using professional channel management software to prevent double-bookings and manage guest messages across all platforms. Your property availability is updated in real-time across Airbnb, Booking.com, Expedia, VRBO, and all other platforms simultaneously — ensuring zero conflicts and maximum efficiency.
                </p>
              </div>
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-refresh-line text-5xl text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Partner With ARM?</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Join property owners who trust ARM for professional management, guaranteed income, and peace of mind. Let's discuss how we can maximize your property's potential.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <i className="ri-message-3-line text-2xl text-teal-600"></i>
                    <a href="sms:732-213-2063" className="text-lg text-gray-900 hover:text-teal-600 transition-colors cursor-pointer">
                      732-213-2063
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-mail-line text-2xl text-blue-600"></i>
                    <a href="mailto:Staying.arm@gmail.com" className="text-lg text-gray-900 hover:text-blue-600 transition-colors cursor-pointer">
                      Staying.arm@gmail.com
                    </a>
                  </div>
                </div>
                <div className="mt-8">
                  <a 
                    href="mailto:Staying.arm@gmail.com?subject=Property Partnership Inquiry"
                    className="inline-block px-8 py-4 bg-gradient-to-r from-teal-600 to-blue-600 text-white font-semibold rounded-lg hover:from-teal-700 hover:to-blue-700 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
                  >
                    Contact Us Today
                  </a>
                </div>
              </div>
              <div 
                className="bg-cover bg-center min-h-96"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20property%20management%20consultation%2C%20confident%20business%20meeting%20between%20property%20owner%20and%20management%20team%2C%20modern%20office%20setting%20with%20colorful%20contemporary%20decor%2C%20handshake%20agreement%2C%20successful%20partnership%2C%20bright%20and%20professional%20atmosphere%2C%20trust%20and%20reliability%2C%20vibrant%20welcoming%20environment&width=800&height=800&seq=arm-contact-v2&orientation=squarish')`
                }}
              ></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner with ARM - Expandable Section - MOVED TO END */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Why Partner With ARM?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Click each section below to learn about our comprehensive partnership program designed to maximize your income while minimizing your responsibilities
            </p>
          </div>

          <div className="space-y-6">
            {partnershipDetails.map((section, index) => {
              const isOpen = openAccordion === index;
              
              return (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-transparent hover:border-teal-200 transition-all duration-300">
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-14 h-14 bg-gradient-to-r ${section.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <i className={`${section.icon} text-2xl text-white`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 text-left">{section.title}</h3>
                    </div>
                    <div className={`w-10 h-10 flex items-center justify-center transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}>
                      <i className="ri-arrow-down-s-line text-3xl text-teal-600"></i>
                    </div>
                  </button>
                  
                  <div className={`overflow-hidden transition-all duration-500 ${
                    isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="px-8 pb-8">
                      <div className="border-t-2 border-gray-100 pt-6">
                        <ul className="space-y-4">
                          {section.details.map((detail, detailIndex) => (
                            <li key={detailIndex} className="flex items-start space-x-3">
                              <div className="w-6 h-6 bg-gradient-to-r from-teal-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-check-line text-sm text-white"></i>
                              </div>
                              <span className="text-lg text-gray-700 leading-relaxed">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link
              to="/landlord-application"
              className="inline-block px-10 py-5 bg-gradient-to-r from-teal-600 to-blue-600 text-white text-lg font-semibold rounded-xl hover:from-teal-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-text-line mr-2"></i>
              Contact Us to Partner
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
